/**
 * What failed, in words an operator can act on (satellite-v02): the call or
 * the action, Firebase's error code, the time, and the message when it adds
 * something beyond the code. A bare "internal" says none of that.
 */
export interface AdminFailure {
  /** The callable's name ("getAdminUserStats") or the action ("Saving the model"). */
  what: string;
  /** Firebase error code without its "functions/" prefix ("internal", "permission-denied"). */
  code: string | null;
  /** The error's message when it says more than the code. */
  detail: string | null;
  at: Date;
  /** Cloud Function behind it, for the Cloud Logging link. */
  fn: string | null;
}

export function describeFailure(what: string, err: unknown, options: { fn?: string | null; now?: Date } = {}): AdminFailure {
  const raw = err as { code?: unknown; message?: unknown } | null | undefined;
  const code = typeof raw?.code === "string" ? raw.code.replace(/^functions\//, "") : null;
  const message = typeof raw?.message === "string" ? raw.message.trim() : typeof err === "string" ? err.trim() : "";
  const generic = !message || message.toLowerCase() === code?.toLowerCase() || /^internal$/i.test(message);
  return { what, code, detail: generic ? null : message, at: options.now ?? new Date(), fn: options.fn ?? null };
}

/** A callable failure: named after the function, linked to its logs. */
export function describeCallableError(fn: string, err: unknown, now?: Date): AdminFailure {
  return describeFailure(fn, err, { fn, now });
}

/**
 * Runs an admin action backed by the callable `fn` and answers what failed,
 * or null when it worked (ux-admin-09): Dismiss on an alert and the Reasoning
 * selects show the failure instead of an unhandled rejection.
 */
export async function callableFailure(fn: string, run: () => Promise<unknown>, now?: Date): Promise<AdminFailure | null> {
  try {
    await run();
    return null;
  } catch (err) {
    return describeCallableError(fn, err, now);
  }
}

/**
 * The admin gate's claim check (ux-admin-11). `resolve` answers whether the
 * account holds the `admin` claim; a rejection means the check could not run
 * (network, a `claimAdminBootstrap` cold start), so the gate offers Retry
 * instead of saying the account is not an admin.
 */
export type AdminClaimCheck =
  | { status: "admin" }
  | { status: "not_admin" }
  | { status: "unverified"; failure: AdminFailure };

export async function checkAdminClaim(resolve: () => Promise<boolean>, now?: Date): Promise<AdminClaimCheck> {
  try {
    return (await resolve()) ? { status: "admin" } : { status: "not_admin" };
  } catch (err) {
    const code = (err as { code?: unknown } | null)?.code;
    // A functions/* code comes from claimAdminBootstrap: link its logs.
    const fn = typeof code === "string" && code.startsWith("functions/") ? "claimAdminBootstrap" : null;
    return { status: "unverified", failure: describeFailure("Checking admin access", err, { fn, now }) };
  }
}

/** «getAdminUserStats failed (internal) · 14:32». */
export function failureTitle(failure: AdminFailure): string {
  const time = failure.at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  return `${failure.what} failed${failure.code ? ` (${failure.code})` : ""} · ${time}`;
}

/**
 * Cloud Logging for one 2nd-gen function (a Cloud Run service named after
 * it, lowercased), warnings and up, from the failure's time back an hour.
 */
export function cloudLoggingUrl(fn: string, projectId: string, at: Date): string {
  const query = [
    'resource.type="cloud_run_revision"',
    `resource.labels.service_name="${fn.toLowerCase()}"`,
    "severity>=WARNING",
  ].join("\n");
  const end = new Date(at.getTime() + 5 * 60_000).toISOString();
  const start = new Date(at.getTime() - 60 * 60_000).toISOString();
  return `https://console.cloud.google.com/logs/query;query=${encodeURIComponent(query)};timeRange=${start}%2F${end}?project=${encodeURIComponent(projectId)}`;
}
